import React, { Component } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import RadioConditional from "../../components/RadioConditional";
import RadioQuestion from "../../components/RadioQuestion";
import TextQuestion from "../../components/TextQuestion";
import DateOfBirthQuestion from "../../components/DateOfBirthQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import { YES_NO_OPTIONS } from "../../constants";
import {
  isDateValid,
  isDayValid,
  isMonthValid,
  isYearValid,
  validateNationalInsuranceNumber,
} from "../../shared/utils/validation";

const yesNoCallMeOptions = [
  { title: "Yes", value: true },
  { title: "No, I prefer to be called...", value: false },
];

class Client1MoreInfo extends Component {
  constructor(props) {
    super(props);

    const errorMessages = {
      annualIncome: "Enter your annual income",
      dateOfBirth: "Enter your date of birth",
      nationality: "Enter your nationality",
      occupation: "Enter your occupation",
      companyName: "Enter the name of the company you work for or current work situation",
      nationalInsuranceNumber: "Enter your National Insurance number",
      hasMiddleNames: "Let us know if you have any middle names",
      middleNames: "Please fill this in",
      hasPreviousName: "Let us know if you have changed your last name",
      previousName: "Please fill this in",
      firstNameIsGivenName: "Let us know the name we should use",
      givenName: "Please fill this in",
      sellerIsRegisteredOwner: "Please choose an option",
    };

    const defaultClient = {
      annualIncome: "",
      dateOfBirth: {
        day: "",
        month: "",
        year: "",
      },
      nationality: "",
      occupation: "",
      hasMiddleNames: null,
      middleNames: "",
      hasPreviousName: null,
      previousName: "",
      firstNameIsGivenName: null,
      givenName: "",
      companyName: "",
      nationalInsuranceNumber: "",
    };

    this.state = {
      client: { ...defaultClient, ...(props.client || {}) },
      sellerIsRegisteredOwner: props.sellerIsRegisteredOwner ?? null,
      errors: {},
      errorMessages: errorMessages,
      dateErrors: {},
    };
  }

  handleValidation() {
    const { isBuying, isSelling, isRemortgaging } = this.props;
    const fields = { ...this.state.client };
    let errors = { ...this.state.errors };
    let errorMessages = { ...this.state.errorMessages };
    let formIsValid = true;

    // Annual income validation
    if (!fields["annualIncome"]) {
      errors["annualIncome"] = true;
      formIsValid = false;
    }

    // Money validation for buying and remortgaging
    if (fields["annualIncome"] !== "" && !this.isMoneyValid(fields["annualIncome"])) {
      errors["annualIncome"] = true;
      errorMessages["annualIncome"] = "Please enter a valid amount";
      formIsValid = false;
    }

    // Date of birth validation
    if (!fields["dateOfBirth"].day || !fields["dateOfBirth"].month || !fields["dateOfBirth"].year) {
      errors["dateOfBirth"] = true;
      errorMessages["dateOfBirth"] = "Enter your date of birth";
      formIsValid = false;
    } else if (!isDateValid(fields["dateOfBirth"].day, fields["dateOfBirth"].month, fields["dateOfBirth"].year)) {
      errors["dateOfBirth"] = true;
      errorMessages["dateOfBirth"] = "Date of birth must be a real date";
      formIsValid = false;
    } else {
      errors["dateOfBirth"] = false;
      errorMessages["dateOfBirth"] = "Enter your date of birth";
    }

    // Nationality validation
    if (!fields["nationality"]) {
      errors["nationality"] = true;
      formIsValid = false;
    }

    // Occupation validation
    if (!fields["occupation"]) {
      errors["occupation"] = true;
      formIsValid = false;
    }

    // Middle names validation
    if (fields["hasMiddleNames"] === null) {
      errors["hasMiddleNames"] = true;
      formIsValid = false;
    }

    if (fields["hasMiddleNames"] === true) {
      if (!fields["middleNames"]) {
        errors["middleNames"] = true;
        formIsValid = false;
      }
    }

    // The remortgage sign-up still asks for previous and preferred names
    if (!isRemortgaging) {
      if (fields["hasPreviousName"] === null) {
        errors["hasPreviousName"] = true;
        formIsValid = false;
      }

      if (fields["hasPreviousName"] === true && !fields["previousName"]) {
        errors["previousName"] = true;
        formIsValid = false;
      }

      if (fields["firstNameIsGivenName"] === null) {
        errors["firstNameIsGivenName"] = true;
        formIsValid = false;
      }

      if (fields["firstNameIsGivenName"] === false && !fields["givenName"]) {
        errors["givenName"] = true;
        formIsValid = false;
      }
    }

    // Buying-specific validation
    if (isBuying) {
      if (!fields["companyName"]) {
        errors["companyName"] = true;
        formIsValid = false;
      }

      const niResult = validateNationalInsuranceNumber(fields["nationalInsuranceNumber"]);
      if (niResult.hasError) {
        errors["nationalInsuranceNumber"] = true;
        errorMessages["nationalInsuranceNumber"] = niResult.message;
        formIsValid = false;
      } else {
        errors["nationalInsuranceNumber"] = false;
        errorMessages["nationalInsuranceNumber"] = "";
      }
    }

    // Selling-specific validation
    if (isSelling && this.state.sellerIsRegisteredOwner === null) {
      errors["sellerIsRegisteredOwner"] = true;
      formIsValid = false;
    }

    this.setState({ errors, errorMessages });
    return formIsValid;
  }

  handleFieldValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };

      if (!isBlur) {
        errors[field] = false;
      } else {
        if (!prevState.client[field]) {
          errors[field] = true;
        }
      }

      return { errors };
    });
  };

  handleDateFieldValidation = (field) => (datePart) => (value) => {
    this.setState((prevState) => {
      let dateErrors = { ...prevState.dateErrors };
      let errors = { ...prevState.errors };
      let errorMessages = { ...prevState.errorMessages };
      let dateField = { ...(dateErrors[field] || {}) };

      if (datePart === "day") {
        dateField["day"] = !isDayValid(value);
      }

      if (datePart === "month") {
        dateField["month"] = !isMonthValid(value);
      }

      if (datePart === "year") {
        dateField["year"] = !isYearValid(value);
      }

      dateErrors[field] = dateField;

      const allValues = Object.values(dateErrors[field]);
      const dateFieldIsValid = allValues.every((item) => item === false);

      if (!dateFieldIsValid) {
        errors[field] = true;
      } else {
        const dob = { ...prevState.client.dateOfBirth, [datePart]: value };
        if (dob.day && dob.month && dob.year && !isDateValid(dob.day, dob.month, dob.year)) {
          errors[field] = true;
          errorMessages[field] = "Date of birth must be a real date";
        } else {
          errors[field] = false;
          errorMessages[field] = "Enter your date of birth";
        }
      }

      return { errors, errorMessages, dateErrors };
    });
  };

  handleRegisteredOwnerFieldValidation = (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };
      const errorMessages = { ...prevState.errorMessages };

      if (!isBlur) {
        errors["sellerIsRegisteredOwner"] = null;
      } else {
        if (prevState.sellerIsRegisteredOwner === null) {
          errors["sellerIsRegisteredOwner"] = errorMessages["sellerIsRegisteredOwner"];
        }
      }

      return { errors };
    });
  };

  normaliseMoney = (m) => {
    const stripCharsRegex = /[£, ]/g;
    return m.replace(stripCharsRegex, "");
  };

  isMoneyValid = (money) => {
    const allowedCharsRegex = /^[0-9£,. ]+$/;
    if (allowedCharsRegex.test(String(money))) {
      const normalised = this.normaliseMoney(money);

      const decimalPlaceRegex = /^\d+\.?\d{0,2}$/;

      return decimalPlaceRegex.test(String(normalised));
    }
    return false;
  };

  handleMoneyValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };
      let errorMessages = { ...prevState.errorMessages };

      if (!isBlur) {
        errors[field] = false;
      } else {
        if (!prevState.client[field]) {
          errors[field] = true;
          errorMessages[field] = "Enter your annual income";
        } else if (!this.isMoneyValid(prevState.client[field])) {
          errors[field] = true;
          errorMessages[field] = "Please enter a valid amount";
        }
      }

      return { errors, errorMessages };
    });
  };

  handleNationalInsuranceNumberValidation = (field) => (event) => {
    const isBlur = event.type === "blur";
    this.setState((prevState) => {
      let errors = { ...prevState.errors };
      let errorMessages = { ...prevState.errorMessages };

      if (!isBlur) {
        errors[field] = false;
        errorMessages[field] = "";
      } else {
        const result = validateNationalInsuranceNumber(prevState.client[field]);
        errors[field] = result.hasError;
        errorMessages[field] = result.message;
      }

      return { errors, errorMessages };
    });
  };

  handleInputChange = (field) => (event) => {
    let client = { ...this.state.client };
    client[field] = event;
    this.setState({ client });
  };

  handleDateChange = (field) => (event) => {
    let dateOfBirth = { ...this.state.client.dateOfBirth };
    dateOfBirth[field] = event.target.value;
    const updatedClient = { ...this.state.client, dateOfBirth: dateOfBirth };
    this.setState({ client: updatedClient });
  };

  selectSellerIsRegisteredOwner = (siro) => {
    this.setState({ sellerIsRegisteredOwner: siro });
  };

  save = (event) => {
    event.preventDefault();

    if (!this.handleValidation()) {
      return;
    }

    const { isBuying, isSelling } = this.props;
    const payload = { client: { ...this.state.client } };

    // Strip buying-only fields for non-buying modes
    if (!isBuying) {
      delete payload.client.companyName;
      delete payload.client.nationalInsuranceNumber;
    }

    // Include sellerIsRegisteredOwner for selling modes
    if (isSelling) {
      payload.sellerIsRegisteredOwner = this.state.sellerIsRegisteredOwner;
    }

    this.props.onStepComplete(payload);
  };

  canWeUseYourFirstName = () => {
    const { firstName } = this.props;
    return firstName ? "Can we call you ‘" + firstName + "’?" : "Can we call you by your first name?";
  };

  render() {
    const { isBuying, isSelling, isRemortgaging } = this.props;
    const { client, errors, errorMessages } = this.state;

    // Determine intro text based on transaction type
    let introText = "";
    if (isRemortgaging) {
      introText =
        "We need these details to start the legal work for your remortgage. Make sure everything you enter here is correct — the wrong information could hold things up.";
    } else if (isBuying && isSelling) {
      introText =
        "We need these details to start the legal work for your sale and purchase. Make sure everything you enter here is correct — the wrong information could hold things up.";
    } else if (isBuying) {
      introText =
        "We need these details to start the legal work for your purchase. Make sure everything you enter here is correct — the wrong information could hold things up.";
    } else if (isSelling) {
      introText =
        "We need these details to start the legal work for your sale. Make sure everything you enter here is correct — the wrong information could hold things up.";
    }

    return (
      <React.Fragment>
        <ScrollToTop />
        <Card title="Tell us a bit about you" removeTopPadding>
          <p>{introText}</p>

          <DateOfBirthQuestion
            value={client.dateOfBirth}
            onChange={this.handleDateChange}
            validate={this.handleDateFieldValidation("dateOfBirth")}
            validationError={errors.dateOfBirth ? errorMessages.dateOfBirth : null}
          />

          <RadioQuestion
            question="Do you have any middle names?"
            options={YES_NO_OPTIONS}
            inline={true}
            name="hasMiddleNames"
            selected={client.hasMiddleNames}
            optionSelected={this.handleInputChange("hasMiddleNames")}
            validate={this.handleFieldValidation("hasMiddleNames")}
            validationError={errors.hasMiddleNames ? errorMessages.hasMiddleNames : null}
          />
          {client.hasMiddleNames && (
            <RadioConditional>
              <TextQuestion
                question="Middle names"
                value={client.middleNames || ""}
                valueEntered={this.handleInputChange("middleNames")}
                validate={this.handleFieldValidation("middleNames")}
                validationError={errors.middleNames ? errorMessages.middleNames : null}
              />
            </RadioConditional>
          )}

          {!isRemortgaging && (
            <React.Fragment>
              <RadioQuestion
                question="Have you ever changed your last name?"
                options={YES_NO_OPTIONS}
                inline={true}
                name="hasPreviousName"
                selected={client.hasPreviousName}
                optionSelected={this.handleInputChange("hasPreviousName")}
                validate={this.handleFieldValidation("hasPreviousName")}
                validationError={errors.hasPreviousName ? errorMessages.hasPreviousName : null}
              />
              {client.hasPreviousName && (
                <RadioConditional>
                  <TextQuestion
                    question="What was your last name?"
                    value={client.previousName || ""}
                    valueEntered={this.handleInputChange("previousName")}
                    validate={this.handleFieldValidation("previousName")}
                    validationError={errors.previousName ? errorMessages.previousName : null}
                  />
                </RadioConditional>
              )}

              <RadioQuestion
                question={this.canWeUseYourFirstName()}
                options={yesNoCallMeOptions}
                inline={true}
                name="firstNameIsGivenName"
                selected={client.firstNameIsGivenName}
                optionSelected={this.handleInputChange("firstNameIsGivenName")}
                validate={this.handleFieldValidation("firstNameIsGivenName")}
                validationError={errors.firstNameIsGivenName ? errorMessages.firstNameIsGivenName : null}
              />
              {client.firstNameIsGivenName === false && (
                <RadioConditional>
                  <TextQuestion
                    question="What name would you like us to use?"
                    value={client.givenName || ""}
                    valueEntered={this.handleInputChange("givenName")}
                    validate={this.handleFieldValidation("givenName")}
                    validationError={errors.givenName ? errorMessages.givenName : null}
                  />
                </RadioConditional>
              )}
            </React.Fragment>
          )}

          <TextQuestion
            question="Nationality"
            value={client.nationality || ""}
            valueEntered={this.handleInputChange("nationality")}
            validate={this.handleFieldValidation("nationality")}
            validationError={errors.nationality ? errorMessages.nationality : null}
          />

          <TextQuestion
            question="Your job"
            hint="e.g. teacher, waiter, actor, etc."
            value={client.occupation || ""}
            valueEntered={this.handleInputChange("occupation")}
            validate={this.handleFieldValidation("occupation")}
            validationError={errors.occupation ? errorMessages.occupation : null}
          />

          {isBuying && (
            <TextQuestion
              question="The name of the company you work for"
              hint="If you don't work for a company, briefly describe your current situation (e.g. self-employed, not in work, retired)"
              value={client.companyName || ""}
              valueEntered={this.handleInputChange("companyName")}
              validate={this.handleFieldValidation("companyName")}
              validationError={errors.companyName ? errorMessages.companyName : null}
            />
          )}

          <TextQuestion
            question="Annual income"
            hint="Enter the full amount before tax, including any bonuses"
            value={client.annualIncome || ""}
            valueEntered={this.handleInputChange("annualIncome")}
            validate={this.handleMoneyValidation("annualIncome")}
            validationError={errors.annualIncome ? errorMessages.annualIncome : null}
          />

          {isBuying && (
            // GOV.UK uses QQ 12 34 56 C as a deliberately invalid example
            // so it can never match a real person's NI number
            <TextQuestion
              question="National Insurance number"
              hint="It’s on your payslip. For example, QQ 12 34 56 C"
              value={client.nationalInsuranceNumber || ""}
              valueEntered={this.handleInputChange("nationalInsuranceNumber")}
              validate={this.handleNationalInsuranceNumberValidation("nationalInsuranceNumber")}
              validationError={errors.nationalInsuranceNumber ? errorMessages.nationalInsuranceNumber : null}
            />
          )}

          {isSelling && (
            <RadioQuestion
              question="Are you the registered owner of the property?"
              options={YES_NO_OPTIONS}
              inline={true}
              name="sellerIsRegisteredOwner"
              selected={this.state.sellerIsRegisteredOwner}
              optionSelected={this.selectSellerIsRegisteredOwner}
              validate={this.handleRegisteredOwnerFieldValidation}
              validationError={errors.sellerIsRegisteredOwner ? errorMessages.sellerIsRegisteredOwner : null}
            />
          )}

          <Action onClick={this.save}>Continue</Action>
        </Card>
      </React.Fragment>
    );
  }
}

export default Client1MoreInfo;
